import { useEffect, useState, useCallback } from "react";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader, Loading, EmptyState } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { DataTable } from "@/components/common/DataTable";
import { ResourceForm } from "@/components/common/ResourceForm";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, Trash2, Target, TrendingDown, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

const LEVEL = {
  ok: { bar: "bg-emerald-500", text: "text-emerald-600", label: "On track" },
  warning: { bar: "bg-amber-500", text: "text-amber-600", label: "Near limit" },
  exceeded: { bar: "bg-primary", text: "text-primary", label: "Over budget" },
};

function Meter({ pct, level, forecastPct }) {
  const l = LEVEL[level] || LEVEL.ok;
  return (
    <div className="w-44">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className={cn("font-semibold tabular", l.text)}>{pct}%</span>
        <span className="text-muted-foreground">{l.label}</span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
        <div className={cn("h-full rounded-full transition-all", l.bar)} style={{ width: `${Math.min(pct, 100)}%` }} />
        {forecastPct != null && (
          <div className="absolute top-0 h-full w-0.5 bg-neutral-900" style={{ left: `${Math.min(forecastPct, 100)}%` }} title={`Projected ${forecastPct}%`} />
        )}
      </div>
    </div>
  );
}

export default function Budget() {
  const { can, user } = useAuth();
  const { options, outletOptions, outletName } = useMeta();
  const isSuper = user?.role === "super_admin" || user?.role === "finance";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outlet, setOutlet] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const fields = [
    { name: "name", label: "Budget Name", required: true },
    ...(isSuper ? [{ name: "outlet_id", label: "Outlet", type: "select", span: 1, options: outletOptions, required: true }] : []),
    { name: "category", label: "Category", type: "select", span: 1, options: options("expense_category") },
    { name: "department", label: "Department", type: "select", span: 1, options: options("department") },
    { name: "period", label: "Period", type: "select", span: 1, options: [{ value: "monthly", label: "Monthly" }, { value: "annual", label: "Annual" }], default: "monthly" },
    { name: "year", label: "Year", type: "number", span: 1, default: new Date().getFullYear() },
    { name: "amount", label: "Budget Amount", type: "number", span: 1, step: "0.01", required: true },
    { name: "alert_threshold", label: "Alert Threshold (%)", type: "number", span: 1, step: "1", default: 80 },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/budgets/status", { params: { outlet_id: outlet || undefined } }); setRows(data || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [outlet]);
  useEffect(() => { load(); }, [load]);

  const save = async (values) => {
    setSaving(true);
    try {
      const payload = { ...values, amount: Number(values.amount), alert_threshold: Number(values.alert_threshold) };
      if (editing) await api.put(`/budgets/${editing.id}`, payload);
      else await api.post("/budgets", payload);
      toast.success(editing ? "Budget updated" : "Budget created");
      setFormOpen(false); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    try { await api.delete(`/budgets/${delTarget.id}`); toast.success("Deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiError(e)); setDelTarget(null); }
  };

  const totalBudget = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalActual = rows.reduce((s, r) => s + Number(r.actual || 0), 0);
  const overCount = rows.filter((r) => r.alert_level === "exceeded").length;
  const warnCount = rows.filter((r) => r.alert_level === "warning").length;
  const overForecast = rows.filter((r) => r.forecast_level === "exceeded").length;

  const columns = [
    { key: "name", label: "Budget", render: (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.category || "All categories"} · {r.period}</div></div> },
    ...(isSuper ? [{ key: "outlet_id", label: "Outlet", render: (r) => outletName(r.outlet_id) }] : []),
    { key: "amount", label: "Budget", className: "text-right", cellClassName: "tabular text-right font-semibold", render: (r) => money(r.amount) },
    { key: "actual", label: "Spent", className: "text-right", cellClassName: "tabular text-right", render: (r) => money(r.actual) },
    { key: "remaining", label: "Remaining", className: "text-right", cellClassName: "tabular text-right", render: (r) => <span className={r.remaining < 0 ? "text-primary font-semibold" : ""}>{money(r.remaining)}</span> },
    { key: "pct", label: "Utilization", render: (r) => <Meter pct={r.pct} level={r.alert_level} forecastPct={r.forecast_pct} /> },
    { key: "forecast", label: "Projected (month-end)", className: "text-right", cellClassName: "text-right", render: (r) => (
      <div className="text-right">
        <div className={cn("font-semibold tabular", (LEVEL[r.forecast_level] || LEVEL.ok).text)}>{money(r.forecast)}</div>
        <div className="text-xs text-muted-foreground">{r.forecast_pct}% of budget</div>
      </div>
    ) },
    { key: "alert_threshold", label: "Alert @", className: "text-right", cellClassName: "tabular text-right", render: (r) => `${Math.round(r.alert_threshold ?? 80)}%` },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Budget" subtitle="Outlet & category budgets with live actual-vs-budget and threshold alerts.">
        {isSuper && (
          <Select value={outlet || "all"} onValueChange={(v) => setOutlet(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="budget-outlet-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All outlets</SelectItem>{outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {can("budget:create") && <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-budget-btn"><Plus className="mr-1 h-4 w-4" />New Budget</Button>}
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total Budget" value={money(totalBudget)} icon={Target} accent="bg-primary/10 text-primary" sub={`${rows.length} budgets`} />
        <KpiCard label="Total Spent" value={money(totalActual)} icon={TrendingDown} accent="bg-amber-100 text-amber-700" sub={totalBudget > 0 ? `${Math.round(totalActual / totalBudget * 100)}% used` : ""} />
        <KpiCard label="Near Limit" value={warnCount} icon={AlertTriangle} accent="bg-amber-100 text-amber-700" sub="at threshold" testId="kpi-warn" />
        <KpiCard label="Over Budget" value={overCount} icon={AlertTriangle} accent="bg-red-100 text-red-700" sub={`${overForecast} projected to exceed`} testId="kpi-over" />
      </div>

      {loading ? <Loading /> : rows.length === 0 ? (
        <EmptyState icon={Target} title="No budgets" hint="Create budgets to monitor spending thresholds." />
      ) : (
        <DataTable columns={columns} rows={rows} total={rows.length} testId="budget-table"
          rowActions={(r) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`budget-menu-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {can("budget:edit") && <DropdownMenuItem onClick={() => { setEditing(r); setFormOpen(true); }} data-testid={`edit-${r.id}`}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>}
                {can("budget:delete") && <DropdownMenuItem className="text-destructive" onClick={() => setDelTarget(r)} data-testid={`delete-${r.id}`}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )} />
      )}

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} title={editing ? "Edit Budget" : "New Budget"} fields={fields} initial={editing} onSubmit={save} submitting={saving} />
      <ConfirmDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)} title="Delete budget?" description="This cannot be undone." confirmText="Delete" destructive onConfirm={remove} />
    </div>
  );
}
